import { FirestoreNextPageConfig, FirestorePaginationType, FirestorePreviuosPageConfig, TablePaginationConfig, TablePaginationResponse } from "../types/common";
import { FirestoreQueryConstraint } from "../types/firebase";
import { firestoreGetCountFromServer, firestoreGetNextPage, firestoreGetPreviousPage, firestoreRefreshPage } from "./common/firebase";
import * as firestore from 'firebase/firestore';

export async function getTableNumberOfPages(
  collection: string
): Promise<number> {
  const countSnap = await firestoreGetCountFromServer(collection);
  return countSnap.data().count;
}

export async function getNextTablePage<T>(
  collection: string,
  currentPageConfig: TablePaginationConfig,
  orderBy = 'tsCreated'
): Promise<TablePaginationResponse<T>> {
  const pageData: T[] = [];
  const { size, lastDoc } = currentPageConfig;
  const firestoreNextPageConfig: FirestoreNextPageConfig = { pageSize: size, lastDoc, type: FirestorePaginationType.NEXT };
  const nextPage = await firestoreGetNextPage(collection, firestoreNextPageConfig, orderBy);
  nextPage.forEach((el) => pageData.push(el.data() as T));
  return { 
    data: pageData, 
    cursors: {
      first: nextPage.docs[0],
      second: nextPage.docs[1],
      last: nextPage.docs[nextPage.docs.length - 1]
    }
  };
}

export async function getPreviousTablePage<T>(
  collection: string,
  currentPageConfig: TablePaginationConfig,
  orderBy = 'tsCreated'
): Promise<TablePaginationResponse<T>> {
  const pageData: T[] = [];
  const { size, firstDoc } = currentPageConfig;
  if (!firstDoc) throw 'Invalid firstDoc to fetch previousPage';
  const firestorePreviousPageConfig: FirestorePreviuosPageConfig = { firstDoc, pageSize: size, type: FirestorePaginationType.PREVIOUS };
  const previousPage = await firestoreGetPreviousPage(collection, firestorePreviousPageConfig, orderBy);
  previousPage.forEach((el) => pageData.push(el.data() as T));
  return { 
    data: pageData, 
    cursors: {
      first: previousPage.docs[0],
      second: previousPage.docs[1],
      last: previousPage.docs[previousPage.docs.length - 1]
    }
  };
}

export async function refreshTablePageAfterDelete<T>(
  collection: string,
  currentPageConfig: TablePaginationConfig,
  isFirstDoc: boolean,
  orderBy = 'tsCreated'
): Promise<TablePaginationResponse<T>> {
  const pageData: T[] = [];
  const { size, firstDoc, secondDoc} = currentPageConfig;
  let newPage;
  if (!isFirstDoc) {
    if (!firstDoc) throw 'Invalid cursor';
    newPage = await firestoreRefreshPage(collection, firstDoc, size, orderBy);
  } else {
    if (!secondDoc) throw 'Invalid cursor';
    newPage = await firestoreRefreshPage(collection, secondDoc, size, orderBy);
  }
  newPage.forEach((el) => pageData.push(el.data() as T));
  return {
    data: pageData,
    cursors: {
      first: newPage.docs[0],
      second: newPage.docs[1],
      last: newPage.docs[newPage.docs.length - 1]
    }
  }
}

export async function getMaxPageNumber(
  collection: string,
  size: number,
  queryContraints?: FirestoreQueryConstraint[]
): Promise<number> {
  const snap = await firestoreGetCountFromServer(collection, queryContraints);
  const numberOfDocuments = snap.data().count;
  return Math.ceil(numberOfDocuments / size);
}